import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { extractYouTubeId, youTubeEmbedUrl } from '../utils/youtubeEmbed'

const YouTubeFrame = ({ url }) => {
  const id = extractYouTubeId(url)
  const src = id ? youTubeEmbedUrl(id) : null
  if (!src) return null
  return (
    <div className="aspect-video w-full rounded-lg overflow-hidden border border-admin-200 bg-black">
      <iframe
        title="YouTube"
        className="h-full w-full"
        src={src}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  )
}

const markdownComponents = {
  a({ href, children }) {
    const text = typeof children === 'string' ? children : ''
    if (href && text === href && extractYouTubeId(href)) {
      return <YouTubeFrame url={href} />
    }
    return (
      <a href={href} className="text-primary-600 underline" target="_blank" rel="noreferrer">
        {children}
      </a>
    )
  },
}

export const NotificationEditor = ({
  initial,
  tenants = [],
  tiers = [],
  onSubmit,
  onCancel,
  saving,
}) => {
  const taRef = useRef(null)
  const [title, setTitle] = useState('')
  const [bodyMarkdown, setBodyMarkdown] = useState('')
  const [videoUrl, setVideoUrl] = useState('')
  const [links, setLinks] = useState([{ label: '', url: '' }])
  const [publishAt, setPublishAt] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [audienceType, setAudienceType] = useState('all_tenants')
  const [selectedTenantIds, setSelectedTenantIds] = useState(new Set())
  const [selectedTierIds, setSelectedTierIds] = useState(new Set())
  const [isPublished, setIsPublished] = useState(true)
  const [emojiDraft, setEmojiDraft] = useState('')

  useEffect(() => {
    if (!initial) {
      setTitle('')
      setBodyMarkdown('')
      setVideoUrl('')
      setLinks([{ label: '', url: '' }])
      const now = new Date()
      const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16)
      setPublishAt(local)
      setExpiresAt('')
      setAudienceType('all_tenants')
      setSelectedTenantIds(new Set())
      setSelectedTierIds(new Set())
      setIsPublished(true)
      return
    }
    setTitle(initial.title || '')
    setBodyMarkdown(initial.body_markdown || '')
    setVideoUrl(initial.video_url || '')
    setLinks(
      Array.isArray(initial.links) && initial.links.length
        ? initial.links.map((l) => ({ label: l.label || '', url: l.url || '' }))
        : [{ label: '', url: '' }]
    )
    const pa = initial.publish_at ? String(initial.publish_at).replace('Z', '') : ''
    setPublishAt(pa.includes('.') ? pa.slice(0, 16) : pa.slice(0, 16))
    const ex = initial.expires_at ? String(initial.expires_at).replace('Z', '') : ''
    setExpiresAt(ex ? ex.slice(0, 16) : '')
    setAudienceType(initial.audience_type || 'all_tenants')
    setIsPublished(initial.is_published !== false)
    const tset = new Set()
    const trset = new Set()
    ;(initial.targets || []).forEach((t) => {
      const typ = t.type || t.target_type
      const tid = t.target_id || t.id
      if (typ === 'tenant' && tid) tset.add(tid)
      if (typ === 'subscription_tier' && tid) trset.add(tid)
    })
    setSelectedTenantIds(tset)
    setSelectedTierIds(trset)
  }, [initial])

  const insertAtCaret = useCallback((text) => {
    const el = taRef.current
    if (!el) {
      setBodyMarkdown((b) => b + text)
      return
    }
    const start = el.selectionStart ?? el.value.length
    const end = el.selectionEnd ?? el.value.length
    const v = el.value
    const next = v.slice(0, start) + text + v.slice(end)
    setBodyMarkdown(next)
    requestAnimationFrame(() => {
      el.focus()
      const pos = start + text.length
      el.setSelectionRange(pos, pos)
    })
  }, [])

  const addEmoji = () => {
    const ch = emojiDraft.trim()
    if (ch) insertAtCaret(ch)
    setEmojiDraft('')
  }

  const buildPayload = () => {
    const targets = []
    if (audienceType === 'specific_tenants') {
      selectedTenantIds.forEach((id) => targets.push({ type: 'tenant', id }))
    }
    if (audienceType === 'subscription_tiers') {
      selectedTierIds.forEach((id) => targets.push({ type: 'subscription_tier', id }))
    }
    const cleanLinks = links
      .map((l) => ({ label: (l.label || '').trim(), url: (l.url || '').trim() }))
      .filter((l) => l.label && l.url)
    const pub = publishAt ? new Date(publishAt).toISOString() : new Date().toISOString()
    const exp = expiresAt ? new Date(expiresAt).toISOString() : null
    return {
      title: title.trim(),
      body_markdown: bodyMarkdown,
      video_url: videoUrl.trim() || null,
      links: cleanLinks,
      publish_at: pub,
      expires_at: exp,
      audience_type: audienceType,
      targets,
      is_published: isPublished,
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(buildPayload())
  }

  const tierOptions = useMemo(
    () =>
      (tiers || []).map((t) => ({
        id: t.name,
        label: t.display_name || t.name || t.id,
      })),
    [tiers]
  )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col lg:flex-row gap-6">
      <div className="flex-1 space-y-4 min-w-0">
        <div>
          <label className="block text-sm font-medium text-admin-700 mb-1">Título</label>
          <input
            className="input w-full"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-admin-700 mb-1">Corpo (Markdown)</label>
          <textarea
            ref={taRef}
            className="input w-full min-h-[180px] font-mono text-sm"
            value={bodyMarkdown}
            onChange={(e) => setBodyMarkdown(e.target.value)}
          />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input
              type="text"
              className="input w-32 text-lg"
              placeholder="Emoji"
              value={emojiDraft}
              onChange={(e) => setEmojiDraft(e.target.value)}
              maxLength={8}
            />
            <button type="button" className="btn btn-secondary text-sm" onClick={addEmoji}>
              Inserir emoji
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-admin-700 mb-1">URL do vídeo (YouTube)</label>
          <input
            className="input w-full"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-admin-700 mb-2">Links</label>
          {links.map((row, idx) => (
            <div key={idx} className="flex gap-2 mb-2">
              <input
                className="input flex-1"
                placeholder="Rótulo"
                value={row.label}
                onChange={(e) => {
                  const next = [...links]
                  next[idx] = { ...next[idx], label: e.target.value }
                  setLinks(next)
                }}
              />
              <input
                className="input flex-1"
                placeholder="https://..."
                value={row.url}
                onChange={(e) => {
                  const next = [...links]
                  next[idx] = { ...next[idx], url: e.target.value }
                  setLinks(next)
                }}
              />
              <button
                type="button"
                className="btn btn-secondary px-2"
                onClick={() => setLinks(links.filter((_, i) => i !== idx))}
                aria-label="Remover link"
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => setLinks([...links, { label: '', url: '' }])}
          >
            + Adicionar link
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-admin-700 mb-1">Publicar em</label>
            <input
              type="datetime-local"
              className="input w-full"
              value={publishAt}
              onChange={(e) => setPublishAt(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-admin-700 mb-1">Expira em (opcional)</label>
            <input
              type="datetime-local"
              className="input w-full"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-admin-700 mb-1">Público</label>
          <select
            className="input w-full"
            value={audienceType}
            onChange={(e) => setAudienceType(e.target.value)}
          >
            <option value="all_tenants">Todos os estúdios</option>
            <option value="specific_tenants">Estúdios específicos</option>
            <option value="subscription_tiers">Planos de assinatura</option>
          </select>
        </div>
        {audienceType === 'specific_tenants' && (
          <div className="max-h-48 overflow-y-auto border border-admin-200 rounded-lg p-2 space-y-1">
            {tenants.map((t) => (
              <label key={t.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedTenantIds.has(t.id)}
                  onChange={(e) => {
                    const next = new Set(selectedTenantIds)
                    if (e.target.checked) next.add(t.id)
                    else next.delete(t.id)
                    setSelectedTenantIds(next)
                  }}
                />
                <span>{t.name || t.slug}</span>
              </label>
            ))}
          </div>
        )}
        {audienceType === 'subscription_tiers' && (
          <div className="max-h-48 overflow-y-auto border border-admin-200 rounded-lg p-2 space-y-1">
            {tierOptions.map((t) => (
              <label key={t.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedTierIds.has(t.id)}
                  onChange={(e) => {
                    const next = new Set(selectedTierIds)
                    if (e.target.checked) next.add(t.id)
                    else next.delete(t.id)
                    setSelectedTierIds(next)
                  }}
                />
                <span>{t.label}</span>
              </label>
            ))}
          </div>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isPublished}
            onChange={(e) => setIsPublished(e.target.checked)}
          />
          Publicada (visível dentro do período agendado)
        </label>
        <div className="flex gap-2 pt-2">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
            Cancelar
          </button>
        </div>
      </div>
      <div className="flex-1 min-w-0 border border-admin-200 rounded-xl p-4 bg-white max-h-[70vh] overflow-y-auto">
        <h4 className="text-sm font-semibold text-admin-800 mb-3">Pré-visualização</h4>
        <h2 className="text-xl font-bold text-admin-900 mb-2">{title || 'Título'}</h2>
        {videoUrl ? <YouTubeFrame url={videoUrl} /> : null}
        <div className="prose prose-sm max-w-none mt-3">
          <ReactMarkdown components={markdownComponents}>{bodyMarkdown || '*Vazio*'}</ReactMarkdown>
        </div>
        {links.some((l) => l.label && l.url) && (
          <ul className="mt-4 list-disc pl-5 text-sm">
            {links
              .filter((l) => l.label && l.url)
              .map((l, i) => (
                <li key={i}>
                  <a href={l.url} className="text-primary-600 underline" target="_blank" rel="noreferrer">
                    {l.label}
                  </a>
                </li>
              ))}
          </ul>
        )}
      </div>
    </form>
  )
}
